
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from '@tanstack/react-query';
import { SavedState } from '@/components/team-details/types/selector-types';
import { useFootballFilters } from '@/hooks/useFootballFilters';

export interface Country {
  id: number;
  name: string;
  display_order: number;
}

export const useHomeFilters = () => {
  // State for filters
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedLeague, setSelectedLeague] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  
  // State for table
  const [showTable, setShowTable] = useState(false);
  const [tableData, setTableData] = useState<any>(null);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // State for results
  const [resultsCountry, setResultsCountry] = useState<string | null>(null);
  const [resultsLeague, setResultsLeague] = useState<string | null>(null);
  const [orderedCountries, setOrderedCountries] = useState<string[]>([]);
  
  // Queries
  const {
    data: countriesData,
    isLoading: isLoadingCountries
  } = useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('countries')
        .select('*')
        .order('display_order');
      if (error) throw error;
      return data as Country[];
    }
  });
  
  // Optimized query - only fetch metadata, not the full data
  const {
    data: savedStates,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['saved-states-metadata'],
    queryFn: async () => {
      console.log('Fetching saved states metadata...');
      try {
        const { data, error } = await supabase
          .from('saved_states')
          .select('id, name, country, league, year, created_at, display_order')
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching saved states:', error);
          throw error;
        }
        
        console.log('Raw saved states data:', data);
        
        if (!data || data.length === 0) {
          console.log('No saved states found - returning empty array');
          return [];
        }
        
        console.log('Fetched saved states metadata:', data.length);
        return data as SavedState[];
      } catch (err) {
        console.error('Query function error:', err);
        throw err;
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
  
  // Use the filter hook
  const {
    availableCountries,
    availableLeagues,
    availableYears,
    filteredStates
  } = useFootballFilters(savedStates || [], selectedCountry, selectedLeague, selectedYear);
  
  const {
    availableCountries: resultsAvailableCountries,
    availableLeagues: resultsAvailableLeagues
  } = useFootballFilters(savedStates || [], resultsCountry, null, null);
  
  // Order countries based on display_order
  useEffect(() => {
    if (countriesData && countriesData.length > 0 && resultsAvailableCountries.length > 0) {
      const orderedCountryNames = countriesData
        .map(country => country.name)
        .filter(name => resultsAvailableCountries.includes(name));
      console.log('Setting ordered countries:', orderedCountryNames);
      setOrderedCountries(orderedCountryNames);
    } else if (resultsAvailableCountries.length > 0) {
      setOrderedCountries(resultsAvailableCountries);
    }
  }, [countriesData, resultsAvailableCountries.join(',')]);
  
  // Handlers
  const handleYearSelect = async (year: string) => {
    setSelectedYear(year);
    if (selectedCountry && selectedLeague) {
      console.log('Fetching data for:', { selectedCountry, selectedLeague, year });
      try {
        const { data: stateData, error } = await supabase
          .from('saved_states')
          .select('*')
          .eq('country', selectedCountry)
          .eq('league', selectedLeague)
          .eq('year', year)
          .maybeSingle();
        
        if (error) {
          console.error('Error fetching data:', error);
          throw error;
        }
        
        if (stateData && stateData.data) {
          console.log('Found matching data:', stateData.id);
          setTableData(stateData.data);
          setShowTable(true);
        } else {
          console.log('No data found for selection');
          setTableData(null);
          setShowTable(false);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        setTableData(null);
        setShowTable(false);
      }
    }
  };
  
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection(current => current === 'asc' ? 'desc' : 'asc');
  };
  
  const getFilteredAndSortedData = () => {
    if (!tableData) return [];
    return [...tableData].sort((a, b) => {
      if (!sortColumn) return 0;
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      return sortDirection === 'asc' 
        ? aValue > bValue ? 1 : -1 
        : aValue < bValue ? 1 : -1;
    });
  };

  return {
    // States
    selectedCountry,
    selectedLeague,
    selectedYear,
    showTable,
    tableData,
    sortColumn,
    sortDirection,
    resultsCountry,
    resultsLeague,
    orderedCountries,
    
    // Data
    availableCountries,
    availableLeagues,
    availableYears,
    resultsAvailableLeagues,
    filteredStates,
    savedStates,
    countriesData,
    
    // Loading states
    isLoading,
    isError,
    isLoadingCountries,
    
    // State setters
    setSelectedCountry,
    setSelectedLeague,
    setSelectedYear,
    setShowTable,
    setTableData,
    setSortColumn,
    setSortDirection,
    setResultsCountry,
    setResultsLeague,
    
    // Handlers
    handleYearSelect,
    handleSort,
    getFilteredAndSortedData
  };
};
